import type { Application } from '../../../field-data/useApplications'
import type { LayerSelection } from '../data-layer-state'
import {
  type AsAppliedEquivalencyKey,
  EquivalencyOutcome,
  type LayerEquivalencyKey,
  type LayerScope
} from './layer-scope'

export const scopeOf = (layer: LayerSelection): LayerScope =>
  layer.kind === 'AsApplied'
    ? { kind: 'Stream', streamId: layer.streamId }
    : { kind: 'PlanningField', planningFieldId: layer.planningFieldId }

/**
 * The key a selection currently sits on, or `null` when it has none: a bare
 * field and the editors by construction, and an as-applied selection whose
 * ids are absent from `applications`. A null key means a scope change has
 * nothing to preserve; it is not a failure.
 *
 * `applications` is read only for as-applied selections; the other variants
 * carry their key inline.
 */
export const equivalencyKeyOf = (
  layer: LayerSelection,
  applications: readonly Application[]
): LayerEquivalencyKey | null => {
  switch (layer.kind) {
    case 'AsApplied': {
      const app = applications.find((a) => a.application_id === layer.applicationId)
      const layerEntry = app?.layers.find((l) => l.id === layer.layerId)
      if (app === undefined || layerEntry === undefined) return null
      return {
        kind: 'AsApplied',
        appTypeKey: app.type.key,
        applicationName: app.name,
        layerKey: layerEntry.key
      }
    }
    case 'Satellite':
      return { kind: 'Satellite', imagery: layer.imagery }
    case 'SoilSample':
      return { kind: 'SoilSample', measurement: layer.measurement }
    case 'SoilSurvey':
      return { kind: 'SoilSurvey', attribute: layer.attribute }
    case 'Planning':
      return { kind: 'PlanAsset', assetType: layer.assetType }
    case 'Field':
    case 'PlanEditor':
    case 'PlanAssetEditor':
    case 'BoundaryCreator':
    case 'PrescriptionCreator':
      return null
  }
}

export type LayerMatch = {
  applicationId: string
  layerId: string
  outcome: EquivalencyOutcome
}

/**
 * The layer in `applications` that best honors `key`, relaxing
 * `applicationName` before giving up on the key entirely, then falling back to
 * {@link fallbackLayerIn}.
 *
 * `null` when the stream has no applications.
 */
export const matchAsAppliedLayer = ({
  requested,
  applications
}: {
  requested: AsAppliedEquivalencyKey | null
  applications: readonly Application[]
}): LayerMatch | null => {
  if (requested !== null) {
    const candidates = applications.flatMap((app) =>
      app.type.key === requested.appTypeKey
        ? app.layers.filter((l) => l.key === requested.layerKey).map((layer) => ({ app, layer }))
        : []
    )

    const exact = candidates.find((c) => c.app.name === requested.applicationName)
    if (exact !== undefined) {
      return {
        applicationId: exact.app.application_id,
        layerId: exact.layer.id,
        outcome: EquivalencyOutcome.Exact
      }
    }

    const equivalent = candidates[0]
    if (equivalent !== undefined) {
      return {
        applicationId: equivalent.app.application_id,
        layerId: equivalent.layer.id,
        outcome: EquivalencyOutcome.Equivalent
      }
    }
  }

  const fallback = fallbackLayerIn(applications)
  if (fallback === null) return null
  return {
    ...fallback,
    outcome: requested === null ? EquivalencyOutcome.NotApplicable : EquivalencyOutcome.Substituted
  }
}

/**
 * A layer this stream demonstrably carries, for when no equivalency was
 * honored: the primary application, else the first, and that application's
 * most important layer.
 *
 * Derived from the catalog already in hand rather than from the operation-wide
 * defaults lookup, which answers this same question from this same data and so
 * is only worth its cost to callers who have no catalog yet.
 *
 * Within an application the API orders layers most-important-first,
 * which is what makes `layers[0]` the right pick.
 */
const fallbackLayerIn = (
  applications: readonly Application[]
): { applicationId: string; layerId: string } | null => {
  const primary = applications.find((a) => a.default === true) ?? applications[0]
  const layer = primary?.layers[0]
  if (primary === undefined || layer === undefined) return null
  return { applicationId: primary.application_id, layerId: layer.id }
}
